import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaEnvelope } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

interface FAQItem {
  question: string;
  answer: string;
}

const workerFAQs: FAQItem[] = [
  {
    question: 'How does auto-apply work?',
    answer: 'Paid automation uses your saved preferences to attempt applications where the posting destination supports submission. You can choose review-first mode, pause automation, and review attempt results on your Applications page.',
  },
  {
    question: 'How is my match score calculated?',
    answer: 'Your match score is based on five factors: role fit, certifications, location proximity, rate compatibility, and schedule availability. A score of 70+ is a strong match. Keep your profile complete and up-to-date for the best scores.',
  },
  {
    question: 'What does Automation Early Access include?',
    answer: 'The $9 monthly worker plan includes up to 20 supported automation attempts per week, with review-first and pause controls. Matching, in-app updates, and manual applications remain available for free.',
  },
  {
    question: 'How do I get more gig matches?',
    answer: 'Add the roles, certifications, equipment, rate range, travel radius, and availability you want employers and the matching system to consider. Complete, current information can make a profile easier to evaluate, but StageCrew does not guarantee a specific number of matches or bookings.',
  },
  {
    question: 'How do payments work?',
    answer: 'Payments are handled between you and the employer. StageCrew connects you with gigs — once you\'re booked, payment terms are agreed upon directly with the employer. You can track your earnings on the Payments page.',
  },
  {
    question: 'Can I block specific companies or dates?',
    answer: 'Yes. Go to your Availability Calendar to block specific dates. In your profile settings, you can add companies to your excluded list so you won\'t be matched with them.',
  },
];

const employerFAQs: FAQItem[] = [
  {
    question: 'How do I post a gig?',
    answer: 'Go to your Employer Dashboard and click "Post a Gig." Fill in the job details including roles needed, location, dates, and pay rate. Your first 10 gig posts are free — after that, you\'ll need a subscription.',
  },
  {
    question: 'How many free gig posts do I get?',
    answer: 'Every employer gets 10 free gig posts. Current monthly plans are Starter ($99 for 5 posts), Growth ($249 for 20 posts), and Enterprise ($499 for unlimited posts). Review the pricing or subscription page for the current plan terms before purchasing.',
  },
  {
    question: 'How do I find workers with specific certifications?',
    answer: 'Use the "Find Workers" tab on your Employer Dashboard. You can search by role, certification, location, and availability. Workers\' profiles show their full certification and equipment lists.',
  },
  {
    question: 'How can I contact a worker before booking?',
    answer: 'Use the contact information available in the candidate workflow. StageCrew does not currently provide a button to start a new conversation; existing conversations remain available on the Messages page.',
  },
];

const generalFAQs: FAQItem[] = [
  {
    question: 'Is StageCrew free to sign up?',
    answer: 'Yes. Creating an account is completely free for both workers and employers. Workers can browse jobs and receive matches on the free tier. Employers get 10 free gig posts before needing a subscription.',
  },
  {
    question: 'What types of gigs are on StageCrew?',
    answer: 'StageCrew specializes in AV, stagehand, and live event work — including film, TV, corporate events, concerts, theater, and trade shows. Common roles include audio engineers, lighting techs, stagehands, camera operators, and video engineers.',
  },
  {
    question: 'How do I reset my password?',
    answer: 'Click "Forgot Password" on the login page and enter your email address. You\'ll receive a reset link that\'s valid for 1 hour.',
  },
  {
    question: 'How do I contact support?',
    answer: 'Email us at support@stagecrew.app. Include the account email, page, and a short description of the issue so we can investigate.',
  },
];

const FAQSection: React.FC<{ title: string; items: FAQItem[] }> = ({ title, items }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-lg"
              aria-expanded={openIndex === index}
            >
              <span className="font-medium text-gray-900 pr-4">{item.question}</span>
              {openIndex === index ? (
                <FaChevronUp className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              ) : (
                <FaChevronDown className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              )}
            </button>
            {openIndex === index && (
              <div className="px-5 pb-4 text-gray-600 leading-relaxed">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const HelpPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <SEOHead
        title="Help & FAQ — StageCrew | AV & Stagehand Job Marketplace"
        description="Find answers to common questions about StageCrew — how matching works, auto-apply, payments, employer gig posting, and more."
        canonical="https://stagecrew.app/help"
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900">Help & FAQ</h1>
          <p className="text-gray-600 mt-2">
            Find answers to common questions about StageCrew.
          </p>
        </div>

        <FAQSection title="For Workers" items={workerFAQs} />
        <FAQSection title="For Employers" items={employerFAQs} />
        <FAQSection title="General" items={generalFAQs} />

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Still need help?</h2>
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Reach out and we'll get back to you.
          </p>
          <a
            href="mailto:support@stagecrew.app"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
          >
            <FaEnvelope aria-hidden="true" />
            Contact Support
          </a>
        </div>

        {/* Quick Links */}
        <div className="mt-8 text-center text-sm text-gray-500 space-x-4">
          <Link to="/jobs" className="text-blue-600 hover:underline">Browse Jobs</Link>
          <Link to="/register" className="text-blue-600 hover:underline">Sign Up Free</Link>
          <Link to="/login" className="text-blue-600 hover:underline">Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
